/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useState } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { Datatable } from '../../../components/Datatable/Datatable'
import { Button } from '../../../components/Button/Button'
import { Input } from '../../../components/Input/Input'
import { useNavigate } from 'react-router-dom'
import { Loading } from '../../../components/Loading/Loading'
import { DELETE, GET, PUT } from '../../../services/api'
import Swal from 'sweetalert2'
import { i18n } from '../../../translate/i18n'
import { BreadCrumbs } from '../../../components/BreadCrumbs/breadCrumbrs'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'

enum TypeReviewHotel {
  GENERAL = 'general',
  ROOM = 'room',
  FOOD = 'food',
  SERVICE = 'service',
  CLEANLINESS = 'cleanliness',
  ACTIVITIES = 'activities'
}

interface ReviewHotel {
  id: number
  pluginspaceId: number
  review: string
  rating: number // 1-5 estrelas
  type: TypeReviewHotel
  status: boolean
  created: string
  update: string
  guest?: {
    id: number
    name: string
    email: string
  }
}

const ContaContent: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [reviews, setReviews] = useState<ReviewHotel[]>([])
  const [filteredReviews, setFilteredReviews] = useState<ReviewHotel[]>([])
  const [selectedType, setSelectedType] = useState<string>('all')
  const [selectedRating, setSelectedRating] = useState<string>('all')

  // Opções para filtros
  const typeOptions = [
    { value: 'all', label: i18n.t('hospitality_review.type_options.all_types') },
    { value: TypeReviewHotel.GENERAL, label: i18n.t('hospitality_review.type_options.general') },
    { value: TypeReviewHotel.ROOM, label: i18n.t('hospitality_review.type_options.room') },
    { value: TypeReviewHotel.FOOD, label: i18n.t('hospitality_review.type_options.food') },
    { value: TypeReviewHotel.SERVICE, label: i18n.t('hospitality_review.type_options.service') },
    { value: TypeReviewHotel.CLEANLINESS, label: i18n.t('hospitality_review.type_options.cleanliness') },
    { value: TypeReviewHotel.ACTIVITIES, label: i18n.t('hospitality_review.type_options.activities') },
  ]

  const ratingOptions = [
    { value: 'all', label: i18n.t('hospitality_review.rating_options.all_ratings') },
    { value: '5', label: i18n.t('hospitality_review.rating_options.five_stars') },
    { value: '4', label: i18n.t('hospitality_review.rating_options.four_stars') },
    { value: '3', label: i18n.t('hospitality_review.rating_options.three_stars') },
    { value: '2', label: i18n.t('hospitality_review.rating_options.two_stars') },
    { value: '1', label: i18n.t('hospitality_review.rating_options.one_star') },
  ]

  const getReviewsData = async () => {
    try {
      setLoading(true)
      const res = await GET('/review-hotel')
      
      if (res.success && res.data) {
        // Processar dados para o formato esperado pelo Datatable
        const processedData = res.data.map((item: ReviewHotel) => ({
          ...item,
          date: new Date(item.created).toLocaleString('pt-BR'),
          guestName: item.guest?.name || 'Anônimo',
          typeLabel: getTypeLabel(item.type),
          starsDisplay: generateStarsDisplay(item.rating),
          reviewPreview: item.review.length > 100 ? 
            `${item.review.substring(0, 100)}...` : item.review,
          badge: `<span class="badge ${getTypeColor(item.type)}">${getTypeLabel(item.type)}</span>`,
          stars: `<span class="fs-6 fw-bold ${getRatingColor(item.rating)}">${generateStarsDisplay(item.rating)} (${item.rating}/5)</span>`,
        }))
        
        setReviews(processedData)
        setFilteredReviews(processedData)
      } else {
        setReviews([])
        setFilteredReviews([])
      }
      
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.error('Erro ao carregar reviews:', e)
      Swal.fire({
        icon: 'error',
        title: i18n.t('hospitality_review.messages.error.title').toString(),
        text: i18n.t('hospitality_review.messages.error.load_reviews').toString(),
      })
    }
  }

  const getTypeLabel = (type: TypeReviewHotel): string => {
    const labels = {
      [TypeReviewHotel.GENERAL]: i18n.t('hospitality_review.type_options.general'),
      [TypeReviewHotel.ROOM]: i18n.t('hospitality_review.type_options.room'),
      [TypeReviewHotel.FOOD]: i18n.t('hospitality_review.type_options.food'),
      [TypeReviewHotel.SERVICE]: i18n.t('hospitality_review.type_options.service'),
      [TypeReviewHotel.CLEANLINESS]: i18n.t('hospitality_review.type_options.cleanliness'),
      [TypeReviewHotel.ACTIVITIES]: i18n.t('hospitality_review.type_options.activities'),
    }
    return labels[type] || type
  }

  const generateStarsDisplay = (rating: number): string => {
    const filledStars = '★'.repeat(rating)
    const emptyStars = '☆'.repeat(5 - rating)
    return filledStars + emptyStars
  }

  const getRatingColor = (rating: number): string => {
    if (rating >= 4) return 'text-success'
    if (rating >= 3) return 'text-warning'
    return 'text-danger'
  }

  const getTypeColor = (type: TypeReviewHotel): string => {
    const colors = {
      [TypeReviewHotel.GENERAL]: 'badge-primary',
      [TypeReviewHotel.ROOM]: 'badge-info',
      [TypeReviewHotel.FOOD]: 'badge-warning',
      [TypeReviewHotel.SERVICE]: 'badge-success',
      [TypeReviewHotel.CLEANLINESS]: 'badge-secondary',
      [TypeReviewHotel.ACTIVITIES]: 'badge-dark',
    }
    return colors[type] || 'badge-light'
  }

  const deleteReview = async (id: number) => {
    if (!id) {
      Swal.fire({
        icon: 'error',
        title: i18n.t('hospitality_review.messages.error.title').toString(),
        text: i18n.t('hospitality_review.messages.error.invalid_id').toString(),
      })
      return
    }

    // Confirmação antes de deletar
    const confirmResult = await Swal.fire({
      title: i18n.t('hospitality_review.messages.confirm.delete_title').toString(),
      text: i18n.t('hospitality_review.messages.confirm.delete_text').toString(),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: i18n.t('hospitality_review.messages.confirm.delete_confirm').toString(),
      cancelButtonText: i18n.t('hospitality_review.messages.confirm.delete_cancel').toString(),
    })

    if (!confirmResult.isConfirmed) return

    try {
      setLoading(true)
      const res = await DELETE(`/review-hotel/${id}`)
      
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: i18n.t('hospitality_review.messages.success.review_deleted').toString(),
          text: i18n.t('hospitality_review.messages.success.delete_success').toString(),
        }).then(async () => await init())
      } else {
        throw new Error(res.message || 'Erro ao deletar avaliação')
      }
      
      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: i18n.t('hospitality_review.messages.error.title').toString(),
        text: e.message || i18n.t('hospitality_review.messages.error.delete_error').toString(),
      })
      console.error('Erro ao deletar:', e)
    }
  }

  const switchStatus = async (id: number, status: boolean) => {
    try {
      setLoading(true)
      const res = await PUT(`/review-hotel/${id}`, {
        status: !status,
      })
      
      if (res.success) {
        // Atualizar o estado local para refletir a mudança
        setReviews(prev => 
          prev.map(item => 
            item.id === id ? { ...item, status: !status } : item
          )
        )
        
        // Também atualizar a lista filtrada
        setFilteredReviews(prev => 
          prev.map(item => 
            item.id === id ? { ...item, status: !status } : item
          )
        )
        
        Swal.fire({
          icon: 'success',
          title: i18n.t('hospitality_review.messages.success.status_updated').toString(),
          text: !status ? i18n.t('hospitality_review.messages.success.approved_success').toString() : i18n.t('hospitality_review.messages.success.suspended_success').toString(),
          timer: 2000,
          showConfirmButton: false
        })
      } else {
        throw new Error(res.message || 'Erro ao alterar status')
      }
      
      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: i18n.t('hospitality_review.messages.error.title').toString(),
        text: e.message || i18n.t('hospitality_review.messages.error.status_error').toString(),
      })
      console.error('Erro ao alterar status:', e)
    }
  }

  const applyFilters = () => {
    let filtered = [...reviews]

    // Filtrar por tipo
    if (selectedType !== 'all') {
      filtered = filtered.filter(review => review.type === selectedType)
    }

    // Filtrar por rating
    if (selectedRating !== 'all') {
      filtered = filtered.filter(review => review.rating === parseInt(selectedRating))
    }

    setFilteredReviews(filtered)
  }

  const init = async () => {
    setLoading(true)
    await getReviewsData()
    setLoading(false)
  }

  // Aplicar filtros quando mudarem
  useEffect(() => {
    applyFilters()
  }, [selectedType, selectedRating, reviews])

  useEffect(() => {
    init()
  }, [])

  // Estatísticas dos reviews
  const getStatistics = () => {
    const total = reviews.length
    const approved = reviews.filter(r => r.status).length
    const pending = total - approved
    const averageRating = total > 0 ? 
      (reviews.reduce((sum, r) => sum + r.rating, 0) / total).toFixed(1) : '0'

    return { total, approved, pending, averageRating }
  }

  const stats = getStatistics()

  return (
    <>
      {loading && <Loading />}
      
      <div className='card py-4 px-11'>
        <div className='card-header border-0 pt-5'>
          <div className='row w-100 align-items-center'>
            <div className='col-md-8'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hospitality_review.page_title')}
                </span>
                <BreadCrumbs />
               
              </h3>
            </div>
             
          </div>
        </div>

        {/* Estatísticas */}
        <div className='card-body pb-0'>
          <div className='row mb-6'>
            <div className='col-md-3'>
              <div className='card bg-light-primary'>
                <div className='card-body text-center py-4'>
                  <i className='fas fa-comments fa-2x text-primary mb-2'></i>
                  <h4 className='fw-bold text-primary'>{stats.total}</h4>
                  <span className='text-muted'>{i18n.t('hospitality_review.statistics.total_reviews')}</span>
                </div>
              </div>
            </div>
            <div className='col-md-3'>
              <div className='card bg-light-info'>
                <div className='card-body text-center py-4'>
                  <i className='fas fa-star fa-2x text-info mb-2'></i>
                  <h4 className='fw-bold text-info'>{stats.averageRating}</h4>
                  <span className='text-muted'>{i18n.t('hospitality_review.statistics.average_rating')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className='row mb-6'>
            <div className='col-md-6'>
              <Input
                inputAttr={{
                  type: 'select',
                }}
                label={i18n.t('hospitality_review.filters.filter_by_type')}
                options={typeOptions.map(opt => ({ value: opt.value, select: opt.label }))}
                value={selectedType}
                change={(value: any) => setSelectedType(value)}
              />
            </div>
            <div className='col-md-6'>
              <Input
                inputAttr={{
                  type: 'select',
                }}
                label={i18n.t('hospitality_review.filters.filter_by_rating')}
                options={ratingOptions.map(opt => ({ value: opt.value, select: opt.label }))}
                value={selectedRating}
                change={(value: any) => setSelectedRating(value)}
              />
            </div>
          </div>
        </div>

        <div className='card-body'>
          <Datatable
            data={filteredReviews}
            headers={[
              { key: i18n.t('hospitality_review.table.headers.rating'), value: 'stars', type: 'html' },
              { key: i18n.t('hospitality_review.table.headers.type'), value: 'badge', type: 'html' },
              { key: i18n.t('hospitality_review.table.headers.comment'), value: 'reviewPreview', type: 'default' },
              { key: i18n.t('hospitality_review.table.headers.date'), value: 'date', type: 'default' },
              { key: i18n.t('hospitality_review.table.headers.actions'), value: 'acoes', type: '' },
            ]}
            options={[
              {
                icon: 'fas fa-eye',
                title: i18n.t('hospitality_review.table.actions.view_full_review'),
                action: (item) => {
                  Swal.fire({
                    title: `${i18n.t('hospitality_review.modal.review_title')} ${item.guestName || i18n.t('hospitality_review.modal.guest_anonymous')}`,
                    html: `
                      <div style="text-align: left;">
                        <div class="mb-3">
                          <strong>${i18n.t('hospitality_review.modal.fields.rating')}:</strong> 
                          <span class="${getRatingColor(item.rating)}" style="font-size: 1.2em;">
                            ${item.starsDisplay} (${item.rating}/5)
                          </span>
                        </div>
                        <div class="mb-3">
                          <strong>${i18n.t('hospitality_review.modal.fields.type')}:</strong> 
                          <span class="badge ${getTypeColor(item.type)}">${item.typeLabel}</span>
                        </div>
                        <div class="mb-3">
                          <strong>${i18n.t('hospitality_review.modal.fields.status')}:</strong> 
                          <span class="badge ${item.status ? 'badge-success' : 'badge-danger'}">
                            ${item.status ? i18n.t('hospitality_review.table.status.approved') : i18n.t('hospitality_review.table.status.pending')}
                          </span>
                        </div>
                        <div class="mb-3">
                          <strong>${i18n.t('hospitality_review.modal.fields.date')}:</strong> ${item.date}
                        </div>
                        <div class="mb-3">
                          <strong>${i18n.t('hospitality_review.modal.fields.comment')}:</strong>
                          <div style="background: #f8f9fa; padding: 10px; border-radius: 5px; margin-top: 5px;">
                            "${item.review}"
                          </div>
                        </div>
                        ${item.guest?.email ? `<div><strong>${i18n.t('hospitality_review.modal.fields.email')}:</strong> ${item.guest.email}</div>` : ''}
                      </div>
                    `,
                    width: 600,
                    showCloseButton: true,
                    showConfirmButton: false
                  })
                },
              },
            ]}
            color='primary'
          />
        </div>
      </div>
    </>
  )
}

const ReviewsHotel: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          { title: i18n.t('hospitality_review.breadcrumb.dashboard'), path: 'dashboard', isActive: false },
          { title: i18n.t('hospitality_review.breadcrumb.hospitality'), path: 'hospitality', isActive: false },
          { title: i18n.t('hospitality_review.breadcrumb.reviews'), path: 'hospitality/reviews', isActive: true },
        ]}
      >
        {i18n.t('hospitality_review.page_title')}
      </PageTitle>
      <ContaContent />
    </>
  )
}

export { ReviewsHotel }
